import { useEffect, useState } from 'react';
import type { CountryRing } from '@tiw/globe';

/**
 * Load the country-boundaries GeoJSON (pipeline/countries.py) once and index it
 * by ISO-2 code → the country's boundary rings. The globe looks a country up by
 * the code embedded in an IODA incident id (`ioda-DM` → `DM`) to light up its
 * whole shape on a country-level outage (P5). Empty until loaded.
 */
interface CountriesGeoJSON {
  readonly features?: ReadonlyArray<{
    readonly properties?: { readonly iso?: string };
    readonly geometry?: { readonly type?: string; readonly coordinates?: unknown };
  }>;
}

export type CountryShapeIndex = ReadonlyMap<string, readonly CountryRing[]>;

export function useCountryShapes(url: string): CountryShapeIndex {
  const [index, setIndex] = useState<CountryShapeIndex>(() => new Map());

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((r) => (r.ok ? (r.json() as Promise<CountriesGeoJSON>) : Promise.reject(new Error(String(r.status)))))
      .then((geo) => {
        if (cancelled) return;
        const next = new Map<string, CountryRing[]>();
        for (const feature of geo.features ?? []) {
          const iso = feature.properties?.iso;
          const geom = feature.geometry;
          if (!iso || !geom?.coordinates) continue;
          const rings: CountryRing[] = [];
          if (geom.type === 'Polygon') {
            for (const ring of geom.coordinates as unknown[]) rings.push(ring as CountryRing);
          } else if (geom.type === 'MultiPolygon') {
            for (const polygon of geom.coordinates as unknown[][]) {
              for (const ring of polygon) rings.push(ring as CountryRing);
            }
          }
          if (rings.length > 0) next.set(iso.toUpperCase(), rings);
        }
        setIndex(next);
      })
      .catch(() => {
        if (!cancelled) setIndex(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return index;
}
